import { useFormContext, useWatch } from "react-hook-form"
import { findBetterDocSpecialtyByKey } from "@/modules/patients/detail/better-doc/lib/better-doc-sub-specialties"
import { getBetterDocInsurance } from "@/modules/patients/detail/better-doc/lib/get-better-doc-insurance"
import { getBetterDocSpecialty } from "@/modules/patients/detail/better-doc/lib/get-better-doc-specialty"
import { getDeBetterDocInsurance } from "@/modules/patients/detail/better-doc/lib/get-de-better-doc-insurance"
import { usePointOfCareBetterDocContext } from "@/modules/patients/detail/better-doc/point-of-care-better-doc-context"
import type { BetterDocAddress, BetterDocMissingField } from "@/modules/patients/detail/better-doc/types"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { getAppointmentPhoneNumber } from "@/modules/waiting-room/libs/appointment_helper"

export const useBetterDocAvailability = () => {
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()
  const { episodeOfCareProgressNoteData, effectivePointOfCareWithBetterDoc } = usePointOfCareBetterDocContext()

  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)

  if (!patientData.patientById) {
    throw new Error("Patient not found")
  }

  const { address: patientAddress, insurees, phoneNumber: patientPhoneNumber } = patientData.patientById

  const { patientAddress: appointmentAddress } = episodeOfCareProgressNoteData.appointment

  const appointmentPhoneNumber = getAppointmentPhoneNumber(
    episodeOfCareProgressNoteData.appointment.patientPhoneNumber,
    patientPhoneNumber,
  )

  const phoneNumber = appointmentPhoneNumber || patientPhoneNumber

  const address: BetterDocAddress | null =
    appointmentAddress ||
    (patientAddress && {
      countryCode: patientAddress?.countryCode,
      city: patientAddress?.city,
      houseNumberStreet: patientAddress?.houseNumberStreet,
      postalCode: patientAddress?.postalCode,
    }) ||
    null

  const { control } = useFormContext<PointOfCareFormType>()
  const betterDocInInsurance = getBetterDocInsurance(insurees)
  const deBetterDocInsurance = countryCode === "DE" ? getDeBetterDocInsurance(insurees) : null

  const specialtyIdWatch = useWatch({ control, name: "specialtyId" })
  const pointOfCareSpecialtyId = specialtyIdWatch?.id
  const specialtyName =
    countryCode === "DE"
      ? findBetterDocSpecialtyByKey(specialtyIdWatch?.betterDocKey)
      : specialtyIdWatch?.specialty?.name
  const effectivePointOfCare = effectivePointOfCareWithBetterDoc ?? undefined

  const { betterDocSpecialty, isBetterDocSpecialtyFixed } = getBetterDocSpecialty(effectivePointOfCare, specialtyName)

  const missingBetterDocFields: BetterDocMissingField[] = []
  if (betterDocSpecialty === undefined) {
    missingBetterDocFields.push(specialtyIdWatch ? "unsupportedSpecialty" : "specialty")
  }
  if (!betterDocInInsurance) {
    missingBetterDocFields.push("insurance")
  }
  if (!phoneNumber) {
    missingBetterDocFields.push("phoneNumber")
  }
  if (!address) {
    missingBetterDocFields.push("address")
  }

  const hasBetterDocFeature = missingBetterDocFields.length === 0

  return {
    betterDocSpecialty,
    pointOfCareSpecialtyId,
    isBetterDocSpecialtyFixed,
    effectivePointOfCare,
    address,
    phoneNumber,
    hasBetterDocFeature,
    missingBetterDocFields,
    betterDocInInsurance,
    deBetterDocInsurance,
  }
}
